import React, { useState } from "react";
import { Users, Plus, Edit2, Trash2, ShieldCheck, DollarSign } from "lucide-react";
import { useApp } from "../../app/context/AppContext";
import { DataTable } from "../../components/common/DataTable";
import { Badge } from "../../components/common/Badge";
import { formatCurrency } from "../../utils/formatters";
import { Modal } from "../../components/common/Modal";
import { useNotification } from "../../app/context/NotificationContext";

export function CustomerModal({ isOpen, onClose, customerToEdit }) {
  const { addCustomer, updateCustomer } = useApp();
  const { addToast } = useNotification();
  const isEdit = Boolean(customerToEdit);

  const [formData, setFormData] = useState({
    name: "",
    type: "Wholesale",
    segment: "B2B Kirana Partner",
    contactPerson: "",
    phone: "+91 ",
    email: "",
    city: "Mumbai",
    creditLimit: 200000,
    creditDays: 21
  });

  React.useEffect(() => {
    if (customerToEdit) {
      setFormData({
        name: customerToEdit.name || "",
        type: customerToEdit.type || "Wholesale",
        segment: customerToEdit.segment || "B2B Kirana Partner",
        contactPerson: customerToEdit.contactPerson || "",
        phone: customerToEdit.phone || "+91 ",
        email: customerToEdit.email || "",
        city: customerToEdit.city || "Mumbai",
        creditLimit: customerToEdit.creditLimit || 200000,
        creditDays: customerToEdit.creditDays || 21
      });
    } else {
      setFormData({
        name: "",
        type: "Wholesale",
        segment: "B2B Kirana Partner",
        contactPerson: "",
        phone: "+91 ",
        email: "",
        city: "Mumbai",
        creditLimit: 200000,
        creditDays: 21
      });
    }
  }, [customerToEdit, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEdit) {
        await updateCustomer(customerToEdit.id, formData);
        addToast(`Customer "${formData.name}" updated!`, "success");
      } else {
        await addCustomer(formData);
        addToast(`New customer account created for "${formData.name}"!`, "success");
      }
      onClose();
    } catch (err) {
      addToast("Failed to save customer account.", "error");
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? "Edit Customer Account" : "Register New Customer / B2B Account"}
      subtitle="Setup customer credit limits, payment cycles, and commercial terms"
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4 text-xs">
        <div>
          <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
            Customer Business Name / Full Name *
          </label>
          <input
            type="text"
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g. Sharma General Store (Bhandup)"
            className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
              Account Segment *
            </label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl"
            >
              <option value="Wholesale">Wholesale (B2B Bulk)</option>
              <option value="Retail">Retail (Household Member)</option>
            </select>
          </div>

          <div>
            <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
              Business Category
            </label>
            <input
              type="text"
              value={formData.segment}
              onChange={(e) => setFormData({ ...formData, segment: e.target.value })}
              placeholder="e.g. Restaurant, Kirana Shop"
              className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
              Contact Person
            </label>
            <input
              type="text"
              value={formData.contactPerson}
              onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
              className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl"
            />
          </div>

          <div>
            <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
              Phone Number *
            </label>
            <input
              type="text"
              required
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl font-mono"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 bg-slate-50 dark:bg-slate-950 p-3 rounded-2xl border border-slate-200 dark:border-slate-800">
          <div>
            <label className="block text-slate-500 font-semibold mb-1">Credit Limit (₹) *</label>
            <input
              type="number"
              value={formData.creditLimit}
              onChange={(e) => setFormData({ ...formData, creditLimit: Number(e.target.value) })}
              className="w-full px-2.5 py-1.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg font-mono font-bold"
            />
          </div>

          <div>
            <label className="block text-slate-500 font-semibold mb-1">Credit Period (Days)</label>
            <input
              type="number"
              value={formData.creditDays}
              onChange={(e) => setFormData({ ...formData, creditDays: Number(e.target.value) })}
              className="w-full px-2.5 py-1.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg font-mono"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-slate-200 dark:border-slate-800">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-xl"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-xl shadow-lg shadow-emerald-600/30"
          >
            Save Customer Account
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function CustomersModule() {
  const { customers, deleteCustomer } = useApp();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [customerToEdit, setCustomerToEdit] = useState(null);

  const columns = [
    {
      header: "Customer Account",
      accessorKey: "name",
      render: (val, row) => (
        <div>
          <span className="font-bold text-slate-900 dark:text-white">{val}</span>
          <p className="text-[10px] text-slate-400">{row.segment} • {row.city}</p>
        </div>
      )
    },
    {
      header: "Type",
      accessorKey: "type",
      render: (val) => <Badge variant={val === "Wholesale" ? "blue" : "emerald"}>{val}</Badge>
    },
    {
      header: "Credit Limit",
      accessorKey: "creditLimit",
      align: "right",
      render: (val, row) => (
        <div>
          <span className="font-mono font-bold text-slate-900 dark:text-white">{formatCurrency(val)}</span>
          <p className="text-[10px] text-slate-400 font-mono">{row.creditDays}d terms</p>
        </div>
      )
    },
    {
      header: "Outstanding Balance",
      accessorKey: "outstandingBalance",
      align: "right",
      render: (val) => (
        <span
          className={`font-mono font-bold ${
            val > 0 ? "text-rose-600 dark:text-rose-400" : "text-emerald-600 dark:text-emerald-400"
          }`}
        >
          {formatCurrency(val)}
        </span>
      )
    },
    {
      header: "YTD Sales",
      accessorKey: "totalSalesYTD",
      align: "right",
      render: (val) => <span className="font-mono text-slate-700 dark:text-slate-300">{formatCurrency(val)}</span>
    },
    {
      header: "Actions",
      accessorKey: "actions",
      sortable: false,
      render: (_, row) => (
        <div className="flex items-center gap-1.5 justify-end">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setCustomerToEdit(row);
              setIsModalOpen(true);
            }}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800"
            title="Edit Customer"
          >
            <Edit2 className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (window.confirm(`Delete customer account ${row.name}?`)) {
                deleteCustomer(row.id);
              }
            }}
            className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40"
            title="Delete Customer"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white font-display flex items-center gap-2">
            <Users className="w-5 h-5 text-emerald-500" />
            Customer Directory & Credit Accounts
          </h2>
          <p className="text-xs text-slate-500">
            Retail vs wholesale customer segments, credit limits, outstanding balances, and purchase histories
          </p>
        </div>

        <button
          onClick={() => {
            setCustomerToEdit(null);
            setIsModalOpen(true);
          }}
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold rounded-xl shadow-lg shadow-emerald-600/30 transition"
        >
          <Plus className="w-4 h-4" />
          Add Customer Account
        </button>
      </div>

      <DataTable
        columns={columns}
        data={customers}
        title="Customer Accounts Register"
        subtitle="Manage credit terms and aging receivables"
        filename="kirana_customers"
      />

      <CustomerModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        customerToEdit={customerToEdit}
      />
    </div>
  );
}
